/**
 * Product Stock Availability
 * Displays real-time stock information on product detail page
 * Fetches stock data from backend API using variant SKU
 */

const _log_stock_availability =
  typeof window.createLogger === 'function'
    ? window.createLogger('ProductStockAvailability')
    : { log: () => {}, warn: () => {}, error: () => {} };

class ProductStockAvailability {
  constructor() {
    this.apiClient = window.externalApiClient;
    this.utilities = window.StockDateUtils;

    if (!this.apiClient || !this.utilities) {
      _log_stock_availability.error('Dependencies not available');
      return;
    }

    this.init();
  }

  /**
   * Initialize stock availability checker
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupListeners());
    } else {
      this.setupListeners();
    }
  }

  /**
   * Setup event listeners for SKU changes and quantity updates
   */
  setupListeners() {
    _log_stock_availability.log('Setting up event listeners');

    // Listen for custom event from resetPricesAndStockComponents() when variant changes
    document.addEventListener('productSKUChanged', (event) => {
      _log_stock_availability.log('SKU changed event received:', event.detail.sku);
      this.handleSKUChange(event.detail.sku);
    });

    // Listen for quantity changes via PubSub event from QuantityInput custom element
    // The QuantityInput class in scripts.js publishes PUB_SUB_EVENTS.productQuantityUpdate when +/- buttons are clicked
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      _log_stock_availability.log('Setting up PubSub listener for productQuantityUpdate');

      window.subscribe(window.PUB_SUB_EVENTS.productQuantityUpdate, (event) => {
        _log_stock_availability.log('→ Quantity update event received:', event);
        _log_stock_availability.log('→ New quantity:', event.quantity);
        this.handleQuantityChange(event.quantity);
      });
    } else {
      _log_stock_availability.warn('PubSub system not available, will fallback to DOM listeners');

      // Fallback: Listen directly to the quantity-input element's change event
      //   const qtyWrapper = document.querySelector('quantity-input');
      //   if (qtyWrapper) {
      //     console.log('[ProductStockAvailability] Found quantity-input custom element, attaching fallback listener');
      //     qtyWrapper.addEventListener('change', () => {
      //       console.log('[ProductStockAvailability] → quantity-input change event fired');
      //       this.handleQuantityChange();
      //     });
      //   }
    }

    // Display initial stock on page load
    setTimeout(() => this.displayInitialStock(), 500);
  }

  /**
   * Display stock for the initially selected variant on page load
   */
  displayInitialStock() {
    _log_stock_availability.log('Displaying initial stock');
    // Get SKU directly from DOM element and dispatch custom event
    this.updateStock(this.getCurrentSkuFromDom(), 1);
  }

  getCurrentSkuFromDom() {
    const skuElement = document.querySelector('[id^="ProductSku-"]');
    const productSKUValue = skuElement ? skuElement.textContent.trim() : '';
    if (productSKUValue && productSKUValue !== '') {
      _log_stock_availability.log('Initial SKU found in DOM element:', productSKUValue);
      return productSKUValue;
    } else {
      _log_stock_availability.log('Initial SKU element not found or empty.');
      return null;
    }
  }

  /**
   * Handle SKU change from custom event
   * @param {string} sku - The SKU value from the event
   */
  handleSKUChange(sku) {
    _log_stock_availability.log('SKU change handler called with SKU:', sku);
    this.updateStock(sku, 1);
  }

  /**
   * Handle quantity changes to recalc delivery timelines
   */
  handleQuantityChange(quantity) {
    const sku = this.getCurrentSkuFromDom();
    _log_stock_availability.log('Quantity change detected, updating stock for SKU:', sku);
    this.updateStock(sku, quantity);
  }

  /**
   * Get current cart quantity for a specific SKU using Shopify Cart API
   * @param {string} sku - Product SKU
   * @returns {Promise<number>} - Total quantity of this SKU in cart
   */
  async getCartQuantityBySku(sku) {
    try {
      _log_stock_availability.log('getCartQuantityBySku() called with SKU:', sku);

      // Fetch cart using Shopify Cart API
      const response = await fetch('/cart.js');

      if (!response.ok) {
        _log_stock_availability.error('Failed to fetch cart:', response.status);
        return 0;
      }

      const cart = await response.json();
      _log_stock_availability.log('Cart data fetched:', cart);

      if (!cart.items || cart.items.length === 0) {
        _log_stock_availability.log('Cart is empty');
        return 0;
      }

      // Calculate total quantity for this SKU
      const totalQty = cart.items.reduce((total, item) => {
        const itemSku = item.sku || '';
        _log_stock_availability.log('Comparing SKU - Looking for:', sku, 'Item SKU:', itemSku);
        return itemSku === sku ? total + item.quantity : total;
      }, 0);

      _log_stock_availability.log('Total quantity for SKU', sku, ':', totalQty);
      return totalQty;
    } catch (error) {
      _log_stock_availability.error('Error fetching cart:', error);
      return 0;
    }
  }

  /**
   * Show full-page loader
   */
  showLoader() {
    try {
      const existing = document.getElementById('product-stock-loader');
      if (existing) return;

      // Inject spinner styles
      if (!document.getElementById('product-stock-loader-style')) {
        const style = document.createElement('style');
        style.id = 'product-stock-loader-style';
        style.textContent = `
          @keyframes nfy-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .nfy-spinner { width: 40px; height: 40px; border: 4px solid rgba(0,0,0,0.08); border-top-color: #111827; border-radius: 50%; animation: nfy-spin 1s linear infinite; }
        `;
        document.head.appendChild(style);
      }

      const container = document.createElement('div');
      container.id = 'product-stock-loader';
      container.style.cssText =
        'position: fixed; left: 0; top: 0; width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.6); z-index: 9999;';

      const spinner = document.createElement('div');
      spinner.className = 'nfy-spinner';

      container.appendChild(spinner);
      document.body.appendChild(container);
    } catch (e) {
      _log_stock_availability.warn('showLoader error:', e);
    }
  }

  /**
   * Hide full-page loader
   */
  hideLoader() {
    try {
      const loader = document.getElementById('product-stock-loader');
      if (loader?.parentNode) {
        loader.parentNode.removeChild(loader);
      }
    } catch (e) {
      _log_stock_availability.warn('hideLoader error:', e);
    }
  }

  /**
   * Convert 1 PM UK time to user's local time and format it
   * @returns {string} - Formatted local time string (e.g., "1:00 PM" or "13:00")
   */
  getLocalTimeFor1PMUK() {
    try {
      const now = new Date();

      // Get today's date in UK timezone
      const ukDateParts = now
        .toLocaleString('en-CA', {
          timeZone: 'Europe/London',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
        .split('-');
      const year = parseInt(ukDateParts[0]);
      const month = parseInt(ukDateParts[1]) - 1; // Month is 0-indexed
      const day = parseInt(ukDateParts[2]);

      // Calculate UK timezone offset
      // Create a test UTC date and see what time it is in UK
      const testUTC = new Date(Date.UTC(year, month, day, 12, 0, 0));
      const ukHour = parseInt(
        testUTC.toLocaleString('en-US', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          hour12: false
        })
      );

      // Calculate offset: UK is ahead of UTC by (ukHour - 12) hours
      // GMT: 12:00 UTC = 12:00 UK, so offset = 0
      // BST: 12:00 UTC = 13:00 UK, so offset = +1 hour
      const offsetHours = ukHour - 12;

      // Create UTC date for 1 PM UK time
      // 1 PM UK = 13:00 UK = 13:00 - offsetHours in UTC
      const uk1PMUTC = new Date(Date.UTC(year, month, day, 13 - offsetHours, 0, 0));

      // Format this UTC time in the user's local timezone
      const localTime = uk1PMUTC.toLocaleString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      // Check if current local time is past the computed local 1PM UK time
      // To compare times, create a new Date for today at the local equivalent of 1PM UK time
      const targetTimeLocal = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        uk1PMUTC.getHours(),
        uk1PMUTC.getMinutes(),
        0,
        0
      );
      if (now.getTime() > targetTimeLocal.getTime()) {
        return 'Order will be dispatched next working day.';
      }
      return `If order placed before ${localTime}, order will be dispatch today.`;
    } catch (error) {
      _log_stock_availability.warn('Error converting UK time to local time:', error);
      // Fallback to showing UK time if conversion fails
      return 'If order placed before 1:00 PM UK time, order will be dispatched today.';
    }
  }

  /**
   * Update stock display for selected variant
   * @param {string} sku - Product SKU
   */
  async updateStock(sku, addToCartQty) {
    // try {
    //   console.log('[ProductStockAvailability] updateStock() called with SKU:', sku);
    //   if (!sku || sku === 'null' || sku === 'undefined' || sku === '') {
    //     this.clearDisplay();
    //     return;
    //   }
    //   console.log('[ProductStockAvailability] Showing loader...');
    //   this.showLoader();
    //   console.log('[ProductStockAvailability] Fetching stock data from API...');
    //   const stockData = await this.apiClient.fetchStockData([sku]);
    //   console.log('[ProductStockAvailability] Stock data received, size:', stockData.size);
    //   if (stockData.size === 0) {
    //     console.warn('[ProductStockAvailability] No stock data returned from API for SKU:', sku);
    //     this.clearDisplay();
    //     this.hideLoader();
    //     return;
    //   }
    //   const stock = stockData.get(sku);
    //   console.log('[ProductStockAvailability] Stock data for SKU:', stock);
    //   if (!stock) {
    //     console.warn('[ProductStockAvailability] Stock object is null/undefined for SKU:', sku);
    //     this.clearDisplay();
    //     this.hideLoader();
    //     return;
    //   }
    //   console.log('[ProductStockAvailability] Getting cart quantity for SKU:', sku);
    //   let cartQty = await this.getCartQuantityBySku(sku);
    //   console.log('[ProductStockAvailability] Cart quantity:', cartQty);
    //   if (addToCartQty > 1) {
    //     cartQty += addToCartQty;
    //   } else {
    //     cartQty = cartQty > 0 ? cartQty : 1;
    //   }
    //   console.log('[ProductStockAvailability] Rendering stock display...');
    //   const html = this.renderStockDisplay(stock, cartQty);
    //   console.log('[ProductStockAvailability] Rendered HTML:', html);
    //   console.log('[ProductStockAvailability] Inserting HTML into container...');
    //   this.insertHtml(html);
    //   console.log('[ProductStockAvailability] Hiding loader...');
    //   this.hideLoader();
    //   console.log('[ProductStockAvailability] Stock update completed successfully');
    // } catch (error) {
    //   console.error('[ProductStockAvailability] Error updating stock:', error);
    //   console.error('[ProductStockAvailability] Error stack:', error.stack);
    //   this.hideLoader();
    // }
  }

  /**
   * Render stock availability HTML
   * @param {Object} stock - Stock data from API
   * @param {number} cartQty - Current quantity in cart
   * @returns {string} - HTML string
   */
  renderStockDisplay(stock, cartQty) {
    _log_stock_availability.log('Rendering stock display with stock data:', stock, 'and cartQty:', cartQty);
    const inStockQty = parseInt(stock.in_stock_quantity, 10) || 0;
    const pendingOrderQty = parseInt(stock.pending_ordered_quantity, 10) || 0;
    const leadTimeDays = parseInt(stock.lead_time_in_days, 10) || 0;
    const dailyCapacity = Math.max(parseInt(stock.daily_production_capacity, 10) || 1, 1);
    const manufacturingTypeId = stock.manufacturing_type_id;
    const manufacturingTypeName = stock.manufacturing_type_name || '';
    const isManufactured = manufacturingTypeId === 1 || manufacturingTypeName === 'Manufactured';

    // Available quantity for new orders = in_stock - pending_orders
    const availableQty = Math.max(inStockQty - pendingOrderQty, 0);

    _log_stock_availability.log(
      'Stock calculation - inStockQty:',
      inStockQty,
      'pendingOrderQty:',
      pendingOrderQty,
      'availableQty:',
      availableQty
    );

    // Use inline span so it sits beside the SKU span on one line
    let html = `<span class="product-stock-inline">`;

    const remaining = Math.max(cartQty - availableQty, 0);
    const isFullyInStock = remaining <= 0;

    // Match SKU text style for alignment and visual consistency
    const primaryTextStyle =
      'display:inline; margin-left: 0.75rem; font-size: calc(var(--font-body-scale) * 1.3rem); color: #425aa8; font-weight: var(--font-bolder-weight); letter-spacing: -0.01em;';
    const secondaryTextStyle =
      'display:inline; margin-left: 0.75rem; font-size: calc(var(--font-body-scale) * 1.3rem); color: #1f2937;';

    // Shipping message content to be placed in #product-stock-shipping-date
    let shippingText = '';

    if (isFullyInStock) {
      // All requested quantity is in stock → ready to ship immediately
      html += `<span style="${primaryTextStyle}">Ready to ship</span>`;
      const localTime = this.getLocalTimeFor1PMUK();
      shippingText = localTime;
    } else {
      // Not all quantity is in stock → show friendly production + shipping estimate
      let productionDays = leadTimeDays;

      if (isManufactured) {
        const manufacturingDays = Math.max(Math.ceil(remaining / dailyCapacity), 1);
        productionDays += manufacturingDays;
        _log_stock_availability.log(
          'Manufactured backorder - remaining:',
          remaining,
          'productionDays:',
          productionDays
        );
      } else {
        // Non-manufactured backorder uses lead time as prep window
        if (productionDays < 1) productionDays = 1;
        _log_stock_availability.log(
          'Non-manufactured backorder - remaining:',
          remaining,
          'productionDays:',
          productionDays
        );
      }

      if (productionDays < 1) productionDays = 1;

      const shipDate = this.utilities.addWorkingDays(new Date(), productionDays);
      const formattedShipDate = this.utilities.formatDate(Math.floor(shipDate.getTime() / 1000));

      html += `<span style="${primaryTextStyle}">Ready to ship</span>`;
      shippingText = `Estimated shipping: ${formattedShipDate}`;
    }

    html += `</span>`;

    // Update shipping date span content separately
    this.insertShippingText(shippingText);

    // Show backorder warning if needed (only for non-manufactured items)
    // if (!isManufactured && cartQty > 0 && inStockQty > 0 && inStockQty - cartQty < 0) {
    //   html += this.renderBackorderWarning(stock, cartQty);
    // }

    return html;
  }

  /**
   * Render backorder warning message
   * @param {Object} stock - Stock data from API
   * @param {number} cartQty - Current quantity in cart
   * @returns {string} - HTML string for backorder message
   */
  renderBackorderWarning(stock, cartQty) {
    const inStockQty = parseInt(stock.in_stock_quantity, 10) || 0;
    const remaining = cartQty - inStockQty;
    const leadTimeDays = parseInt(stock.lead_time_in_days, 10) || 0;
    const dailyCapacity = Math.max(parseInt(stock.daily_production_capacity, 10) || 1, 1);
    const manufacturingTypeId = stock.manufacturing_type_id;
    const manufacturingTypeName = stock.manufacturing_type_name || '';
    const isManufactured = manufacturingTypeId === 1 || manufacturingTypeName === 'Manufactured';

    let manufacturingDays = 0;
    if (isManufactured) {
      manufacturingDays = Math.ceil(remaining / dailyCapacity);
      if (manufacturingDays < 1) manufacturingDays = 1;
    }

    const productionDays = leadTimeDays + manufacturingDays;
    const deliveryDate = this.utilities.addWorkingDays(new Date(), productionDays);
    const formattedDate = this.utilities.formatDate(Math.floor(deliveryDate.getTime() / 1000));
    const styles = this.utilities.getStockMessageStyles();

    return `
    <div class="cart-shipping-note" style="${styles}">
      <strong>Note:</strong> You already have ${cartQty} of this item in your cart.<br>
      Your additional order will be prepared and shipped together.
      <ul style="margin-top: 0.5em; padding-left: 1.2em; list-style-type: disc;">
        <li>${inStockQty} ${inStockQty === 1 ? 'item is' : 'items are'} currently in stock.</li>
        <li>${remaining} additional ${remaining === 1 ? 'item is' : 'items are'} being made for you and will be ready in ${productionDays} ${productionDays === 1 ? 'day' : 'days'}.</li>
      </ul>
      <p style="margin-top: 0.5em;"><strong>Estimated delivery: ${formattedDate}</strong></p>
    </div>
    `;
  }

  /**
   * Insert rendered HTML into container
   * @param {string} html - HTML to insert
   */
  insertHtml(html) {
    const container = document.getElementById('product-stock-availability');

    if (container) {
      _log_stock_availability.log('Container found, inserting HTML');
      _log_stock_availability.log('Container ID:', container.id);
      _log_stock_availability.log('HTML to insert:', html);
      container.innerHTML = html;
      _log_stock_availability.log('HTML successfully inserted');
    } else {
      _log_stock_availability.error('Container element not found');
      _log_stock_availability.error('Looking for element with ID: product-stock-availability');

      // Debug: List all elements with product-stock in their ID
      const allElements = document.querySelectorAll('[id*="product-stock"]');
      _log_stock_availability.log('Found elements with product-stock in ID:', allElements.length);
      allElements.forEach((el, index) => {
        _log_stock_availability.log(`  [${index}] ID: ${el.id}, Class: ${el.className}`);
      });

      // Debug: List all product-info-meta-item elements
      const metaItems = document.querySelectorAll('.product-info-meta-item');
      _log_stock_availability.log('Found .product-info-meta-item elements:', metaItems.length);
      metaItems.forEach((el, index) => {
        _log_stock_availability.log(`  [${index}] ID: ${el.id || 'no-id'}, Class: ${el.className}`);
      });
    }
  }

  /**
   * Insert shipping message into dedicated span
   * @param {string} text - Shipping message content
   */
  insertShippingText(text) {
    try {
      const el = document.getElementById('product-stock-shipping-date');
      if (!el) {
        _log_stock_availability.warn('Shipping date span not found');
        return;
      }
      if (!text || text.trim() === '') {
        el.innerHTML = '';
        return;
      }
      const style = 'font-size: calc(var(--font-body-scale) * 1.3rem); color: #1f2937;';
      el.innerHTML = `<span class="product-stock-shipping" style="${style}">${text}</span>`;
    } catch (e) {
      _log_stock_availability.warn('Failed to insert shipping text:', e);
    }
  }

  /**
   * Clear stock display
   */
  clearDisplay() {
    const container = document.getElementById('product-stock-availability');
    if (container) {
      container.innerHTML = '';
    }
    const containerShippingDate = document.getElementById('product-stock-shipping-date');
    if (containerShippingDate) {
      containerShippingDate.innerHTML = '';
    }
  }
}
// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductStockAvailability();
  });
} else {
  new ProductStockAvailability();
}
